import { useCallback } from 'react';

interface FolderData {
    id: string;
    name: string;
    level: number;
    parentId?: string | null;
    expanded: boolean;
}

interface DragDropHandlersProps {
    isMobile: boolean;
    folders: FolderData[];
    folderedThreadIds: Set<string>;
    threadHistory: any;
    assignThreadToFolder: (folderId: string, threadId: string, skipReload?: boolean) => void;
    removeThreadFromFolder: (threadId: string, skipReload?: boolean) => void;
    moveFolderToParent: (folderId: string, newParentId: string) => void;
    moveFolderToRoot: (folderId: string) => void;
    toggleFolder: (folderId: string) => void;
    loadFolders: () => Promise<void>;
    setDraggedThreadId: (id: string | undefined) => void;
    setDragOverTimeGroups: (over: boolean) => void;
    setDragFolderId: (id: string | undefined) => void;
    setShowDropZone: (show: boolean) => void;
    setDragOverFolderId: (id: string | undefined) => void;
    setTouchDraggedId: (id: string | undefined) => void;
    setTouchDragType: (type: 'thread' | 'folder' | undefined) => void;
    setTouchDragElement: (element: HTMLElement | undefined) => void;
    setIsDragging: (dragging: boolean) => void;
    setDragOverRoot: (over: boolean) => void;
    setTouchPosition: (position: { x: number; y: number } | null) => void;
    setTouchDragWidth: (width: number | null) => void;
    touchHoldTimer: NodeJS.Timeout | null;
    setTouchHoldTimer: (timer: NodeJS.Timeout | null) => void;
    dragOverFolderId?: string;
    dragFolderId?: string;
    draggedThreadId?: string;
    touchDraggedId?: string;
    touchDragType?: 'thread' | 'folder';
}

export function useDragDropHandlers({
    isMobile,
    folders,
    folderedThreadIds,
    threadHistory,
    assignThreadToFolder,
    removeThreadFromFolder,
    moveFolderToParent,
    moveFolderToRoot,
    toggleFolder,
    loadFolders,
    setDraggedThreadId,
    setDragOverTimeGroups,
    setDragFolderId,
    setShowDropZone,
    setDragOverFolderId,
    setTouchDraggedId,
    setTouchDragType,
    setTouchDragElement,
    setIsDragging,
    setDragOverRoot,
    setTouchPosition,
    setTouchDragWidth,
    touchHoldTimer,
    setTouchHoldTimer,
    dragOverFolderId,
    dragFolderId,
    draggedThreadId,
    touchDraggedId,
    touchDragType
}: DragDropHandlersProps) {
    // Thread drag handlers
    const handleDragStart = useCallback((e: React.DragEvent, threadId: string) => {
        e.dataTransfer.setData('text/plain', threadId);
        e.dataTransfer.setData('type', 'thread');
        e.dataTransfer.effectAllowed = 'move';
        setDraggedThreadId(threadId);
        setIsDragging(true);

        // Add mobile-specific drag image for better visual feedback
        if (isMobile) {
            const thread = threadHistory?.threads?.find(
                (t: any) => t.id === threadId
            );
            const dragImage = document.createElement('div');
            dragImage.textContent = `💬 ${thread?.name || 'Moving thread...'}`;
            dragImage.style.position = 'absolute';
            dragImage.style.top = '-1000px';
            dragImage.style.background = 'rgba(0,0,0,0.8)';
            dragImage.style.color = 'white';
            dragImage.style.padding = '8px 12px';
            dragImage.style.borderRadius = '6px';
            dragImage.style.fontSize = '14px';
            dragImage.style.maxWidth = '200px';
            dragImage.style.whiteSpace = 'nowrap';
            dragImage.style.overflow = 'hidden';
            dragImage.style.textOverflow = 'ellipsis';
            document.body.appendChild(dragImage);
            e.dataTransfer.setDragImage(dragImage, 0, 0);
            setTimeout(() => document.body.removeChild(dragImage), 0);
        }
    }, [isMobile, threadHistory, setDraggedThreadId]);

    const handleDragEnd = useCallback(() => {
        setDraggedThreadId(undefined);
        setDragOverTimeGroups(false);
        setDragOverFolderId(undefined);
        setIsDragging(false);
    }, [setDraggedThreadId, setDragOverTimeGroups, setDragOverFolderId, setIsDragging]);

    // Folder drag handlers
    const handleFolderDragStart = useCallback((e: React.DragEvent, folderId: string) => {
        console.log('🚀 handleFolderDragStart:', folderId);
        e.dataTransfer.setData('text/plain', folderId);
        e.dataTransfer.setData('type', 'folder');
        e.dataTransfer.effectAllowed = 'move';
        setDragFolderId(folderId);
        setIsDragging(true);
        console.log('✅ setDragFolderId called with:', folderId);

        // Show drop zone immediately since it's at the bottom now
        setShowDropZone(true);

        // Add mobile-specific drag image for better visual feedback
        if (isMobile) {
            const dragImage = document.createElement('div');
            dragImage.textContent = '📁 Moving folder...';
            dragImage.style.position = 'absolute';
            dragImage.style.top = '-1000px';
            dragImage.style.background = 'rgba(0,0,0,0.8)';
            dragImage.style.color = 'white';
            dragImage.style.padding = '8px 12px';
            dragImage.style.borderRadius = '6px';
            dragImage.style.fontSize = '14px';
            document.body.appendChild(dragImage);
            e.dataTransfer.setDragImage(dragImage, 0, 0);
            setTimeout(() => document.body.removeChild(dragImage), 0);
        }
    }, [isMobile, setDragFolderId, setShowDropZone]);

    const handleFolderDragEnd = useCallback(() => {
        console.log('🛑 handleFolderDragEnd called - clearing drag state');
        setDragFolderId(undefined);
        setDragOverFolderId(undefined);
        setDragOverRoot(false);
        setDraggedThreadId(undefined);
        setDragOverTimeGroups(false);
        setShowDropZone(false);
        setIsDragging(false);
    }, [setDragFolderId, setDragOverFolderId, setDragOverRoot, setDraggedThreadId, setDragOverTimeGroups, setShowDropZone, setIsDragging]);

    const handleFolderDragOver = useCallback((e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        // Don't allow dropping a folder onto itself or its children
        const draggedId = e.dataTransfer.getData('text/plain');
        const dragType = e.dataTransfer.getData('type');

        if (dragType === 'folder' && draggedId) {
            // Prevent dropping folder onto itself
            if (draggedId === folderId) return;

            // Prevent dropping folder onto its children (would create cycle)
            const draggedFolder = folders.find((f) => f.id === draggedId);
            const targetFolder = folders.find((f) => f.id === folderId);

            if (draggedFolder && targetFolder) {
                // Check if target is a descendant of dragged folder
                const isDescendant = (
                    parentId: string,
                    childId: string
                ): boolean => {
                    const child = folders.find((f) => f.id === childId);
                    if (!child || !child.parentId) return false;
                    if (child.parentId === parentId) return true;
                    return isDescendant(parentId, child.parentId);
                };

                if (isDescendant(draggedId, folderId)) return;

                // Check level limit - target folder's level + 1 should not exceed max depth
                if (targetFolder.level >= 3) return; // Max 4 levels (0,1,2,3)
            }
        }

        setDragOverFolderId(folderId);
    }, [folders, setDragOverFolderId]);

    const handleFolderDragLeave = useCallback((folderId: string, e: React.DragEvent) => {
        // Only clear if we're actually leaving and not entering a child element
        const relatedTarget = e.relatedTarget as HTMLElement;
        const currentTarget = e.currentTarget as HTMLElement;
        
        // Check if we're entering a child element of this folder
        if (relatedTarget && currentTarget.contains(relatedTarget)) {
            return; // Don't clear if entering a child
        }
        
        setDragOverFolderId(undefined);
    }, [setDragOverFolderId]);

    const handleFolderDrop = useCallback(async (e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        const draggedId = e.dataTransfer.getData('text/plain');
        const dragType = e.dataTransfer.getData('type');

        // Check if folder was collapsed before drop
        const targetFolder = folders.find(f => f.id === folderId);
        const wasCollapsed = targetFolder && !targetFolder.expanded;

        if (dragType === 'thread' && draggedId) {
            await assignThreadToFolder(folderId, draggedId, true); // Skip reload for drag operations
            // Expand after the assignment
            if (wasCollapsed) {
                await toggleFolder(folderId);
            }
        } else if (dragType === 'folder' && draggedId) {
            await moveFolderToParent(draggedId, folderId);
            // Expand after the move and reload is complete
            if (wasCollapsed) {
                await toggleFolder(folderId);
            }
        }

        setDragOverFolderId(undefined);
        setDragFolderId(undefined);
    }, [assignThreadToFolder, moveFolderToParent, toggleFolder, folders, setDragOverFolderId, setDragFolderId]);

    // Time groups drag handlers for removing threads from folders
    const handleTimeGroupsDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        // Use state variable instead of dataTransfer (which doesn't work in dragover)
        // Check if the currently dragged thread is in a folder
        if (draggedThreadId && folderedThreadIds.has(draggedThreadId)) {
            setDragOverTimeGroups(true);
        }
    }, [draggedThreadId, folderedThreadIds, setDragOverTimeGroups]);

    const handleTimeGroupsDragLeave = useCallback((e: React.DragEvent) => {
        // Only clear if we're actually leaving the time groups area (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverTimeGroups(false);
        }
    }, [setDragOverTimeGroups]);

    const handleTimeGroupsDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const dragType = e.dataTransfer.getData('type');
        const draggedId = e.dataTransfer.getData('text/plain');

        if (
            dragType === 'thread' &&
            draggedId &&
            folderedThreadIds.has(draggedId)
        ) {
            // Remove thread from folder
            removeThreadFromFolder(draggedId, true); // Skip reload for drag operations
        }

        setDragOverTimeGroups(false);
        setDraggedThreadId(undefined);
    }, [folderedThreadIds, removeThreadFromFolder, setDragOverTimeGroups, setDraggedThreadId]);

    const handleRootDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const draggedId = e.dataTransfer.getData('text/plain');
        const dragType = e.dataTransfer.getData('type');

        if (dragType === 'folder' && draggedId) {
            // Move folder to root level (no parent)
            moveFolderToRoot(draggedId);
        }

        setDragOverFolderId(undefined);
        setDragFolderId(undefined);
        setDragOverRoot(false);
    }, [moveFolderToRoot, setDragOverFolderId, setDragFolderId, setDragOverRoot]);

    // Touch drag handlers for mobile
    const handleTouchStart = useCallback(
        (e: React.TouchEvent, id: string, type: 'thread' | 'folder') => {
            if (!isMobile) return;

            // Check if the touch started on the thread-options button or its children
            const target = e.target as HTMLElement;
            if (target.closest('#thread-options') || target.closest('[data-radix-popper-content-wrapper]')) {
                return; // Ignore touches on thread options menu
            }

            const touch = e.touches[0];
            const element = e.currentTarget as HTMLElement;
            
            // Clear any existing timer
            if (touchHoldTimer) {
                clearTimeout(touchHoldTimer);
            }

            // Store initial touch position
            const initialX = touch.clientX;
            const initialY = touch.clientY;

            // Set a timer for 1 second before starting drag
            const timer = setTimeout(() => {
                setTouchPosition({ x: initialX, y: initialY });
                setTouchDraggedId(id);
                setTouchDragType(type);
                setTouchDragElement(element);
                setIsDragging(true);
                
                // Capture the width of the dragged element
                setTouchDragWidth(element.offsetWidth);

                // Add visual feedback
                if (type === 'thread') {
                    setDraggedThreadId(id);
                } else {
                    setDragFolderId(id);
                    // Show drop zone for folder drags on mobile
                    setShowDropZone(true);
                }

                // Add haptic feedback if available
                if (navigator.vibrate) {
                    navigator.vibrate(50);
                }
            }, 1000); // 1 second delay

            setTouchHoldTimer(timer);

            // Don't prevent default here - allow normal scrolling and clicking
        },
        [isMobile, touchHoldTimer, setTouchPosition, setTouchDraggedId, setTouchDragType, setTouchDragElement, setIsDragging, setTouchDragWidth, setDraggedThreadId, setDragFolderId, setShowDropZone, setTouchHoldTimer]
    );

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!isMobile) return;

            // If we're waiting for the hold timer and finger moves, cancel the timer
            if (touchHoldTimer && !touchDraggedId) {
                clearTimeout(touchHoldTimer);
                setTouchHoldTimer(null);
                return;
            }

            // Only proceed if dragging has actually started
            if (!touchDraggedId) return;

            e.preventDefault();
            const touch = e.touches[0];
            
            // Update touch position to follow finger
            setTouchPosition({ x: touch.clientX, y: touch.clientY });
            
            const elementBelow = document.elementFromPoint(
                touch.clientX,
                touch.clientY
            );

            // Reset all drag over states
            setDragOverFolderId(undefined);
            setDragOverRoot(false);
            setDragOverTimeGroups(false);

            if (elementBelow) {
                // Check if over a folder
                const folderElement = elementBelow.closest('[data-folder-id]');
                if (folderElement) {
                    const folderId =
                        folderElement.getAttribute('data-folder-id');
                    if (folderId && folderId !== touchDraggedId) {
                        setDragOverFolderId(folderId);
                    }
                }

                // Check if over root projects area
                const rootElement = elementBelow.closest('[data-root-drop]');
                if (rootElement && touchDragType === 'folder') {
                    setDragOverRoot(true);
                }

                // Check if over time groups area
                const timeGroupsElement =
                    elementBelow.closest('[data-time-groups]');
                if (
                    timeGroupsElement &&
                    touchDragType === 'thread' &&
                    touchDraggedId &&
                    folderedThreadIds.has(touchDraggedId)
                ) {
                    setDragOverTimeGroups(true);
                }
            }
        },
        [isMobile, touchHoldTimer, touchDraggedId, touchDragType, folderedThreadIds, setTouchHoldTimer, setTouchPosition, setDragOverFolderId, setDragOverRoot, setDragOverTimeGroups]
    );

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            if (!isMobile) return;

            // Clear the hold timer if it's still pending
            if (touchHoldTimer) {
                clearTimeout(touchHoldTimer);
                setTouchHoldTimer(null);
            }

            // Only process drop if dragging actually started
            if (!touchDraggedId || !touchDragType) return;

            const touch = e.changedTouches[0];
            const elementBelow = document.elementFromPoint(
                touch.clientX,
                touch.clientY
            );

            if (elementBelow) {
                // Handle folder drop
                const folderElement = elementBelow.closest('[data-folder-id]');
                if (folderElement) {
                    const folderId =
                        folderElement.getAttribute('data-folder-id');
                    if (folderId && folderId !== touchDraggedId) {
                        if (touchDragType === 'thread') {
                            assignThreadToFolder(folderId, touchDraggedId, true); // Skip reload for drag operations
                        } else if (touchDragType === 'folder') {
                            moveFolderToParent(touchDraggedId, folderId);
                        }
                    }
                }

                // Handle root drop
                const rootElement = elementBelow.closest('[data-root-drop]');
                if (rootElement && touchDragType === 'folder') {
                    moveFolderToRoot(touchDraggedId);
                }

                // Handle time groups drop
                const timeGroupsElement =
                    elementBelow.closest('[data-time-groups]');
                if (
                    timeGroupsElement &&
                    touchDragType === 'thread' &&
                    folderedThreadIds.has(touchDraggedId)
                ) {
                    removeThreadFromFolder(touchDraggedId, true); // Skip reload for drag operations
                }
            }

            // Clean up
            setTouchDraggedId(undefined);
            setTouchDragType(undefined);
            setTouchDragElement(undefined);
            setIsDragging(false);
            setDragOverFolderId(undefined);
            setDragOverRoot(false);
            setDragOverTimeGroups(false);
            setDraggedThreadId(undefined);
            setDragFolderId(undefined);
            setTouchPosition(null);
            setTouchDragWidth(null);
            setTouchHoldTimer(null);
            setShowDropZone(false);
        },
        [
            isMobile,
            touchHoldTimer,
            touchDraggedId,
            touchDragType,
            folderedThreadIds,
            assignThreadToFolder,
            moveFolderToParent,
            moveFolderToRoot,
            removeThreadFromFolder,
            setTouchDraggedId,
            setTouchDragType,
            setTouchDragElement,
            setIsDragging,
            setDragOverFolderId,
            setDragOverRoot,
            setDragOverTimeGroups,
            setDraggedThreadId,
            setDragFolderId,
            setTouchPosition,
            setTouchDragWidth,
            setTouchHoldTimer,
            setShowDropZone
        ]
    );

    return {
        handleDragStart,
        handleDragEnd,
        handleFolderDragStart,
        handleFolderDragEnd,
        handleFolderDragOver,
        handleFolderDragLeave,
        handleFolderDrop,
        handleTimeGroupsDragOver,
        handleTimeGroupsDragLeave,
        handleTimeGroupsDrop,
        handleRootDrop,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd
    };
}